import React, {useCallback, useEffect, useState} from 'react';
import {
  IGroup,
  IGroupInvitationCode,
  IGroupMember,
  IGroupRoles,
  ISupplierUserProfile,
} from 'constants/interfaces';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import SecurityIcon from '@material-ui/icons/Security';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {Route} from 'react-router-dom';
import {connect} from 'react-redux';
import {GroupChat} from 'screens/Dashboard/Groups/GroupChat/GroupChat';
import {
  apiAdminGetGroupMembers,
  apiAdminGetGroupRoles,
  apiAdminAddGroupMember,
  apiAdminGrantAdmin,
  apiAdminRevokeAdmin,
  apiGenerateGroupInvitationCode,
} from 'constants/Api';
import {PermissionsService} from 'services/PermissionsService';
import moment from 'moment';
import {Skeleton} from '@material-ui/lab';
import {Breakpoint} from '@material-ui/core/styles/createBreakpoints';

import './GroupDetail.scss';

type GroupDetailProps = {
  user: ISupplierUserProfile;
  group: IGroup;
  width: Breakpoint;
};

export const GroupDetail = (props: GroupDetailProps) => {
  const {user, group} = props;
  const showTabs = props.width === 'xs' || props.width === 'sm';

  const [tabIndex, setTabIndex] = useState(0);
  const [members, setMembers] = useState<IGroupMember[]>([]);
  const [roles, setRoles] = useState<IGroupRoles | null>(null);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [membersPage, setMembersPage] = useState(1);
  const [membersTotalPages, setMembersTotalPages] = useState(1);

  // Invite code
  const [inviteCode, setInviteCode] = useState<IGroupInvitationCode | null>(
    null,
  );
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);

  // Dialogs
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const permissions = new PermissionsService(user);
  const canWrite = permissions.shouldShowGroupsWrite();

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadMembers = useCallback(
    (page = 1) => {
      setIsLoadingMembers(true);
      apiAdminGetGroupMembers(group.groupId, page, 20)
        .then((res) => {
          setMembers(res.members);
          setMembersTotalPages(res.totalPages);
          setMembersPage(page);
        })
        .catch((err) => {
          console.error('Failed to load group members', err);
        })
        .finally(() => setIsLoadingMembers(false));
    },
    [group.groupId],
  );

  const loadRoles = useCallback(() => {
    setIsLoadingRoles(true);
    apiAdminGetGroupRoles(group.groupId)
      .then(setRoles)
      .catch((err) => {
        console.error('Failed to load group roles', err);
      })
      .finally(() => setIsLoadingRoles(false));
  }, [group.groupId]);

  useEffect(() => {
    loadMembers(1);
    loadRoles();
  }, [group.groupId, loadMembers, loadRoles]);

  // ── Action handlers ───────────────────────────────────────────────────────

  const handleAddMember = (farmerId: number) => {
    return apiAdminAddGroupMember(group.groupId, farmerId)
      .then((res) => {
        setSnackbarMessage(res.message || 'Member added successfully');
        loadMembers(membersPage);
        loadRoles();
      })
      .catch((err) => {
        console.error('Failed to add member', err);
        alert('Failed to add member. Please try again.');
        throw err;
      });
  };

  const handleGrantAdmin = (userId: number) => {
    return apiAdminGrantAdmin(group.groupId, userId)
      .then((res) => {
        setSnackbarMessage(res.message || 'Admin role granted');
        loadRoles();
      })
      .catch((err) => {
        console.error('Failed to grant admin', err);
        alert('Failed to grant admin role. Please try again.');
      });
  };

  const handleRevokeAdmin = (userId: number) => {
    return apiAdminRevokeAdmin(group.groupId, userId)
      .then((res) => {
        setSnackbarMessage(res.message || 'Admin role revoked');
        loadRoles();
      })
      .catch((err) => {
        console.error('Failed to revoke admin', err);
        alert('Failed to revoke admin role. Please try again.');
      });
  };

  const handleGenerateInviteCode = () => {
    setIsGeneratingCode(true);
    apiGenerateGroupInvitationCode(group.groupId)
      .then((res) => {
        setInviteCode(res);
        setSnackbarMessage('Invite code generated!');
      })
      .catch((err: any) => {
        const msg = Array.isArray(err)
          ? err.join(' ')
          : err?.message ?? 'Failed to generate invite code.';
        setSnackbarMessage(msg);
      })
      .finally(() => setIsGeneratingCode(false));
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="group-detail">
      {/* ── Top AppBar ── */}
      <AppBar className="app-bar" elevation={0}>
        <Toolbar>
          <Route
            render={({history}) => (
              <IconButton
                edge="start"
                color="inherit"
                onClick={() =>
                  history.location.key
                    ? history.goBack()
                    : history.replace('/groups')
                }
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            )}
          />
          <Typography variant="h6" className="title">
            {group.name}
          </Typography>

          <Box display="flex" flexDirection="row" alignItems="center">
            {canWrite && (
              <>
                <Tooltip title="Add Member" aria-label="add member">
                  <IconButton
                    color="inherit"
                    onClick={() => setShowAddMemberDialog(true)}
                    aria-label="add member"
                    style={{marginLeft: 4}}
                  >
                    <PersonAddIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Generate Invite Code" aria-label="invite code">
                  <span>
                    <IconButton
                      color="inherit"
                      aria-label="invite code"
                      style={{marginLeft: 4}}
                      disabled={isGeneratingCode}
                      onClick={handleGenerateInviteCode}
                    >
                      {isGeneratingCode ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <VpnKeyIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Manage Roles" aria-label="manage roles">
                  <IconButton
                    color="inherit"
                    aria-label="manage roles"
                    style={{marginLeft: 4}}
                    onClick={() => setTabIndex(0)}
                  >
                    <SecurityIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title="Delete Group (coming soon)"
                  aria-label="delete group"
                >
                  <span>
                    <IconButton
                      color="inherit"
                      aria-label="delete group"
                      disabled
                      style={{marginLeft: 4}}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── Mobile Tabs ── */}
      <AppBar
        position="relative"
        elevation={0}
        variant="outlined"
        className="tabs-container"
        style={{display: showTabs ? '' : 'none'}}
      >
        <Tabs
          value={tabIndex}
          onChange={(_: React.ChangeEvent<{}>, newIndex: number) =>
            setTabIndex(newIndex)
          }
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab value={0} label="Details" />
          <Tab value={1} label="Chat" />
        </Tabs>
      </AppBar>

      {/* ── Two-pane Grid ── */}
      <Grid container className="container">
        {/* Left pane — Management */}
        <Grid
          item
          xs={12}
          md={6}
          className="grid-column group-detail-management-column"
          style={{display: showTabs && tabIndex === 1 ? 'none' : ''}}
        >
          <Paper className="order-info-container" variant="outlined">
            {/* 1. Group Info */}
            <Box className="section-container">
              <Box className="header-container">
                <Typography variant="subtitle1">Group Details</Typography>
              </Box>
              <GroupInfoCard
                group={group}
                canWrite={canWrite}
                onAddMember={() => setShowAddMemberDialog(true)}
              />
            </Box>

            {/* 2. Members */}
            <Box className="section-container">
              <Box className="header-container">
                <Typography variant="subtitle1">
                  Members ({group.memberCount ?? members.length})
                </Typography>
              </Box>
              <MembersList
                members={members}
                isLoading={isLoadingMembers}
                page={membersPage}
                totalPages={membersTotalPages}
                onPageChange={loadMembers}
              />
            </Box>

            {/* 3. Roles & Permissions */}
            <Box className="section-container">
              <Box className="header-container">
                <Typography variant="subtitle1">Roles & Permissions</Typography>
              </Box>
              <RolesSection
                roles={roles}
                isLoading={isLoadingRoles}
                canWrite={canWrite}
                onGrantAdmin={handleGrantAdmin}
                onRevokeAdmin={handleRevokeAdmin}
              />
            </Box>

            {/* 4. Invite Code */}
            <Box className="section-container">
              <Box className="header-container">
                <Typography variant="subtitle1">Invite Code</Typography>
                {canWrite && (
                  <Button
                    size="small"
                    startIcon={
                      isGeneratingCode ? (
                        <CircularProgress size={14} />
                      ) : (
                        <VpnKeyIcon />
                      )
                    }
                    color="primary"
                    onClick={handleGenerateInviteCode}
                    disabled={isGeneratingCode}
                  >
                    {inviteCode ? 'Regenerate' : 'Generate'}
                  </Button>
                )}
              </Box>
              <InviteCodeSection
                inviteCode={inviteCode}
                isGenerating={isGeneratingCode}
                canWrite={canWrite}
                onCopied={() =>
                  setSnackbarMessage('Invite code copied to clipboard!')
                }
              />
            </Box>
          </Paper>
        </Grid>

        {/* Right pane — Chat */}
        <Grid
          item
          xs={12}
          md={6}
          className="grid-column group-detail-chat-column"
          style={{display: showTabs && tabIndex === 0 ? 'none' : ''}}
        >
          <GroupChat group={group} header={true} />
        </Grid>
      </Grid>

      {/* ── Dialogs ── */}
      <Route>
        <AddMemberDialog
          isOpen={showAddMemberDialog}
          onClose={() => setShowAddMemberDialog(false)}
          onConfirm={(farmerId) =>
            handleAddMember(farmerId).then(() => setShowAddMemberDialog(false))
          }
        />
      </Route>

      <Snackbar
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        open={!!snackbarMessage}
        onClose={() => setSnackbarMessage('')}
        autoHideDuration={3000}
        message={snackbarMessage}
      />
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

function GroupInfoCard({
  group,
  canWrite,
  onAddMember,
}: {
  group: IGroup;
  canWrite: boolean;
  onAddMember: () => void;
}) {
  return (
    <Box className="group-info-grid">
      <Box className="info-item">
        <Typography className="info-label">Name</Typography>
        <Typography className="info-value">{group.name}</Typography>
      </Box>
      <Box className="info-item">
        <Typography className="info-label">Visibility</Typography>
        <Typography className="info-value">
          {group.isPrivate ? 'Private' : 'Public'}
        </Typography>
      </Box>
      <Box className="info-item">
        <Typography className="info-label">Status</Typography>
        <Typography className="info-value">
          {group.isArchived ? 'Archived' : 'Active'}
        </Typography>
      </Box>
      <Box className="info-item">
        <Typography className="info-label">Created</Typography>
        <Typography className="info-value">
          {moment(group.createdAt).format('YYYY-MM-DD')}
        </Typography>
      </Box>
      {canWrite && (
        <Box className="info-item" style={{gridColumn: 2}}>
          <Button
            size="small"
            startIcon={<PersonAddIcon />}
            color="primary"
            onClick={onAddMember}
            style={{alignSelf: 'flex-start'}}
          >
            Add Member
          </Button>
        </Box>
      )}
      {group.description && (
        <Box className="info-item" style={{gridColumn: '1 / -1'}}>
          <Typography className="info-label">Description</Typography>
          <Typography className="info-value">{group.description}</Typography>
        </Box>
      )}
      {group.owner && (
        <Box className="info-item">
          <Typography className="info-label">Owner</Typography>
          <Typography className="info-value">{group.owner.username}</Typography>
        </Box>
      )}
    </Box>
  );
}

function MembersList(props: {
  members: IGroupMember[];
  isLoading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const {members, isLoading, page, totalPages, onPageChange} = props;

  if (isLoading) {
    return (
      <>
        {[1, 2, 3].map((i) => (
          <Box key={i} mb={1}>
            <Skeleton variant="rect" height={48} style={{borderRadius: 4}} />
          </Box>
        ))}
      </>
    );
  }

  if (!members.length) {
    return (
      <Typography variant="body2" color="textSecondary">
        No members found.
      </Typography>
    );
  }

  return (
    <>
      {members.map((member) => (
        <Box key={member.userId} className="member-row">
          <Avatar src={member.avatar} className="member-avatar">
            {member.username?.[0]?.toUpperCase()}
          </Avatar>
          <Box className="member-info">
            <Typography variant="body2">{member.username}</Typography>
            {member.phone && (
              <Typography variant="caption" color="textSecondary">
                {member.phone}
              </Typography>
            )}
          </Box>
          {member.isOnline && <Box className="online-badge" title="Online" />}
        </Box>
      ))}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="flex-end" mt={1} style={{gap: 8}}>
          <Button
            size="small"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            Prev
          </Button>
          <Typography variant="caption" style={{alignSelf: 'center'}}>
            {page} / {totalPages}
          </Typography>
          <Button
            size="small"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </Box>
      )}
    </>
  );
}

function RolesSection(props: {
  roles: IGroupRoles | null;
  isLoading: boolean;
  canWrite: boolean;
  onGrantAdmin: (userId: number) => void;
  onRevokeAdmin: (userId: number) => void;
}) {
  const {roles, isLoading, canWrite, onGrantAdmin, onRevokeAdmin} = props;

  if (isLoading) {
    return (
      <>
        {[1, 2, 3].map((i) => (
          <Box key={i} mb={1}>
            <Skeleton variant="rect" height={40} style={{borderRadius: 4}} />
          </Box>
        ))}
      </>
    );
  }

  if (!roles) {
    return (
      <Typography variant="body2" color="textSecondary">
        Could not load roles.
      </Typography>
    );
  }

  const adminUserIds = new Set(roles.admins.map((a) => a.userId));

  return (
    <Box>
      {/* Owner */}
      <Box mb={1}>
        <Typography
          variant="caption"
          color="textSecondary"
          style={{textTransform: 'uppercase', letterSpacing: '0.05em'}}
        >
          Owner
        </Typography>
        <Box className="member-row">
          <Avatar className="member-avatar">
            {roles.owner.username?.[0]?.toUpperCase()}
          </Avatar>
          <Box className="member-info">
            <Typography variant="body2">{roles.owner.username}</Typography>
          </Box>
          <Chip
            label="Owner"
            size="small"
            color="primary"
            className="role-chip"
          />
        </Box>
      </Box>

      {/* Admins */}
      {roles.admins.length > 0 && (
        <Box mb={1}>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{textTransform: 'uppercase', letterSpacing: '0.05em'}}
          >
            Admins
          </Typography>
          {roles.admins.map((admin) => (
            <Box key={admin.userId} className="member-row">
              <Avatar className="member-avatar">
                {admin.username?.[0]?.toUpperCase()}
              </Avatar>
              <Box className="member-info">
                <Typography variant="body2">{admin.username}</Typography>
              </Box>
              <Chip label="Admin" size="small" className="role-chip" />
              {canWrite && (
                <Box className="member-actions">
                  <Tooltip title="Revoke Admin">
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => onRevokeAdmin(admin.userId)}
                    >
                      Revoke
                    </Button>
                  </Tooltip>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Members (non-admin) */}
      {roles.members.filter(
        (m) => !adminUserIds.has(m.userId) && m.userId !== roles.owner.userId,
      ).length > 0 && (
        <Box>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{textTransform: 'uppercase', letterSpacing: '0.05em'}}
          >
            Members
          </Typography>
          {roles.members
            .filter(
              (m) =>
                !adminUserIds.has(m.userId) && m.userId !== roles.owner.userId,
            )
            .map((member) => (
              <Box key={member.userId} className="member-row">
                <Avatar className="member-avatar">
                  {member.username?.[0]?.toUpperCase()}
                </Avatar>
                <Box className="member-info">
                  <Typography variant="body2">{member.username}</Typography>
                </Box>
                {canWrite && (
                  <Box className="member-actions">
                    <Tooltip title="Grant Admin">
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => onGrantAdmin(member.userId)}
                      >
                        Make Admin
                      </Button>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
}

function InviteCodeSection(props: {
  inviteCode: IGroupInvitationCode | null;
  isGenerating: boolean;
  canWrite: boolean;
  onCopied: () => void;
}) {
  const {inviteCode, isGenerating, canWrite, onCopied} = props;

  const handleCopy = () => {
    if (!inviteCode?.invitationCode) return;
    navigator.clipboard.writeText(inviteCode.invitationCode).then(onCopied);
  };

  if (isGenerating) {
    return (
      <Box
        display="flex"
        alignItems="center"
        style={{gap: 8, padding: '8px 0'}}
      >
        <CircularProgress size={16} />
        <Typography variant="body2" color="textSecondary">
          Generating invite code…
        </Typography>
      </Box>
    );
  }

  if (!inviteCode) {
    return (
      <Box className="invite-code-placeholder">
        <VpnKeyIcon style={{fontSize: 32, marginBottom: 8, opacity: 0.4}} />
        <Typography variant="body2" color="textSecondary">
          {canWrite
            ? 'Click "Generate" to create an invite code for this group.'
            : 'No invite code has been generated yet.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        style={{
          gap: 8,
          background: '#f5f5f5',
          borderRadius: 6,
          padding: '10px 14px',
          marginBottom: 6,
          border: '1px solid #e0e0e0',
        }}
      >
        <VpnKeyIcon style={{fontSize: 18, color: '#757575', flexShrink: 0}} />
        <Typography
          variant="body1"
          style={{
            fontFamily: 'monospace',
            flex: 1,
            wordBreak: 'break-all',
            fontWeight: 600,
          }}
        >
          {inviteCode.invitationCode}
        </Typography>
        <Tooltip title="Copy to clipboard">
          <Button size="small" color="primary" onClick={handleCopy}>
            Copy
          </Button>
        </Tooltip>
      </Box>
      <Typography variant="caption" color="textSecondary">
        Expires:{' '}
        {moment(inviteCode.inviteCodeExpiryDate).format('DD MMM YYYY, HH:mm')} (
        {moment(inviteCode.inviteCodeExpiryDate).fromNow()})
      </Typography>
    </Box>
  );
}

function AddMemberDialog(props: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (farmerId: number) => Promise<void>;
}) {
  const [farmerId, setFarmerId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = () => {
    const id = parseInt(farmerId, 10);
    if (!id || isNaN(id)) {
      alert('Please enter a valid farmer ID');
      return;
    }
    setIsSubmitting(true);
    props
      .onConfirm(id)
      .then(() => {
        setFarmerId('');
        setIsSubmitting(false);
      })
      .catch(() => setIsSubmitting(false));
  };

  return (
    <Dialog open={props.isOpen} onClose={props.onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Member</DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          Enter the farmer&apos;s user ID to add them to this group.
        </Typography>
        <TextField
          autoFocus
          label="Farmer ID"
          type="number"
          fullWidth
          variant="outlined"
          size="small"
          value={farmerId}
          onChange={(e) => setFarmerId(e.target.value)}
          style={{marginTop: 8}}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          disabled={isSubmitting || !farmerId}
        >
          {isSubmitting ? <CircularProgress size={18} /> : 'Add Member'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const mapStateToProps = (state: any) => ({
  user: state.user,
});

export default connect(mapStateToProps)(GroupDetail);
